import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api/client";

export interface DailyBriefItem {
  stepId: string;
  playId: string;
  workspaceId: string;
  title: string;
  type: string;
  score: number;
  components: {
    urgency: number;
    blockerImpact: number;
    playPriority: number;
    cxoAttention: number;
  };
  estimatedDurationMinutes: number;
  state: string;
  dueAt: string | null;
}

export interface DailyBriefLoad {
  totalMinutes: number;
  capacityMinutes: number;
  heavy: boolean;
}

export interface DailyBriefDeferSuggestion {
  stepId: string;
  reason: string;
}

export interface DailyBrief {
  items: DailyBriefItem[];
  load: DailyBriefLoad;
  deferSuggestions: DailyBriefDeferSuggestion[];
  awaitingReviewCount: number;
  awaitingReview?: DailyBriefItem[];
}

export interface DailyBriefResponse {
  brief: DailyBrief;
  asOf: string;
}

export function useDailyBrief(workspaceId?: string | null) {
  const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
  return useQuery<DailyBriefResponse, Error>({
    queryKey: ["daily-brief", workspaceId ?? "all"],
    queryFn: () => apiGet<DailyBriefResponse>(`/api/me/daily-brief${qs}`),
    staleTime: 30_000,
  });
}

export function useDeferStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) =>
      apiPost(`/api/steps/${stepId}/defer`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily-brief"] });
      qc.invalidateQueries({ queryKey: ["me-steps"] });
    },
  });
}
